export const TERMS_VERSION = "v1.2 — 2025";

export const TERMS_CONTENT = (
  <div className="prose prose-invert max-w-none">
    <h2 className="m-0">MUDO TERMS &amp; CONDITIONS</h2>
    <p className="text-[var(--mudo-text-muted)]">Version 1.2 — 2025</p>
    <p>Operated by Bark Line Studio Pty Ltd (“Bark Line Studio”, “we”, “our”, “us”).</p>

    <h3>1. Overview</h3>
    <p>Mudo is a multi-world digital platform offering tools for ceramic studios, makers, architects, adventurers, and visitors. The platform includes:</p>
    <ul>
      <li><strong>Mudo Ceramics</strong> — studio management, planner, kiln tools, marketplace, workshops</li>
      <li><strong>Mudo Architecture</strong> — project tools, compliance workflows (coming soon)</li>
      <li><strong>Mudo Adventure</strong> — trip planning, inventory, mapping (coming soon)</li>
    </ul>
    <p>By accessing Mudo, you agree to these Terms.</p>

    <h3>2. Definitions</h3>
    <table className="w-full text-sm">
      <thead>
        <tr>
          <th className="text-left pr-4">Term</th>
          <th className="text-left">Meaning</th>
        </tr>
      </thead>
      <tbody>
        <tr><td className="pr-4 align-top">Studio</td><td>A business user offering products, workshops, or services.</td></tr>
        <tr><td className="pr-4 align-top">Visitor</td><td>A non-studio user browsing, booking, or purchasing.</td></tr>
        <tr><td className="pr-4 align-top">Seller</td><td>A Studio listing items/services on Mudo Shop.</td></tr>
        <tr><td className="pr-4 align-top">Workshop</td><td>A class, event, or booking listed by Studios or by Mudo.</td></tr>
        <tr><td className="pr-4 align-top">Mudo Shop</td><td>The marketplace section of the platform.</td></tr>
        <tr><td className="pr-4 align-top">Stripe Connect</td><td>Third-party payment gateway for all transactions.</td></tr>
        <tr><td className="pr-4 align-top">Tools</td><td>Digital features in any Mudo world.</td></tr>
      </tbody>
    </table>

    <h3>3. Nature of the Platform</h3>
    <h4>A. Marketplace Facilitator</h4>
    <p>Mudo acts primarily as a marketplace platform only. We do NOT:</p>
    <ul>
      <li>own or inspect items</li>
      <li>guarantee quality, legality, or safety</li>
      <li>handle delivery</li>
      <li>mediate disputes (except at our discretion)</li>
      <li>provide financial services or escrow</li>
    </ul>
    <h4>B. Mudo-Provided Services (Hybrid Model)</h4>
    <p>Sometimes Mudo may sell its own workshops or digital products, clearly labelled as such.</p>
    <h4>C. SaaS Multi-World Platform</h4>
    <p>Each world has independent tools, subscription features, and user flows, but all fall under these Terms.</p>

    <h3>4. Eligibility</h3>
    <p>You must:</p>
    <ul>
      <li>be at least 16 years old</li>
      <li>have authority if acting for a business</li>
      <li>comply with your local legal obligations</li>
    </ul>

    <h3>5. User Accounts</h3>
    <p>You agree to:</p>
    <ul>
      <li>provide accurate information</li>
      <li>keep login credentials secure</li>
      <li>not share accounts unless authorised</li>
    </ul>
    <p>We may suspend or terminate accounts for:</p>
    <ul>
      <li>fraud</li>
      <li>illegal activity</li>
      <li>chargebacks</li>
      <li>abuse of any platform feature</li>
      <li>violation of Terms</li>
    </ul>

    <h3>6. Listings &amp; Seller Responsibilities</h3>
    <p>Sellers must ensure that:</p>
    <ul>
      <li>descriptions are accurate</li>
      <li>pricing is correct</li>
      <li>items and workshops comply with laws</li>
      <li>taxes/GST are handled appropriately</li>
      <li>products are delivered as described</li>
    </ul>
    <p>Sellers must not list prohibited, harmful, unsafe, or infringing items. Mudo may remove listings at its discretion.</p>

    <h3>7. Offers &amp; Transactions</h3>
    <p>A sale is binding when:</p>
    <ul>
      <li>A Seller accepts an offer, OR</li>
      <li>A Buyer purchases the item/workshop at listed price.</li>
    </ul>
    <p>After acceptance:</p>
    <ul>
      <li>Buyer must pay</li>
      <li>Seller must fulfil</li>
    </ul>
    <p>Cancelling after acceptance may result in penalties or account restrictions.</p>

    <h3>8. Payments, Payouts &amp; Stripe Connect</h3>
    <h4>8.1 Payment Processing</h4>
    <p>All payments are processed through Stripe Connect. Stripe holds funds until release conditions are met.</p>
    <h4>8.2 Mudo is NOT a Financial Institution</h4>
    <p>We do not hold money, manage escrow, guarantee payouts, or take responsibility for Stripe delays or verification issues.</p>
    <h4>8.3 Payout Timing</h4>
    <p>Subject to Stripe’s compliance checks, banking systems, and geographic restrictions.</p>

    <h3>9. Cancellations &amp; Refunds</h3>
    <p>Refunds follow Studio/Seller refund policies, local consumer law, and Stripe’s dispute process. Mudo may help mediate disputes but has no obligation to issue refunds on behalf of Sellers. Mudo-hosted workshops follow the refund rules listed on the event page.</p>

    <h3>10. Workshops &amp; Studio Space Responsibilities</h3>
    <p>Studios offering workshops must provide accurate descriptions, ensure safety and supervision, and maintain compliance with local regulations and insurance.</p>
    <p>Mudo is not liable for injuries, venue issues, equipment failure, staff behaviour, or missed bookings.</p>

    <h3>11. Use of Digital Tools</h3>
    <p>Tools are provided as-is, including but not limited to Planner; Gantt, Kanban, Calendar; Kiln tools; Cost calculators; Material inventory; Architecture workflows; Adventure trip planner. We do not guarantee accuracy or suitability for any purpose.</p>

    <h3>12. AI Tools Disclaimer</h3>
    <p>AI features (e.g., estimators, drafting assistants, scheduling suggestions) may produce incorrect output, miscalculate, or produce unsafe or unrealistic assumptions. Users must validate all AI recommendations. Mudo is not responsible for damages resulting from AI usage.</p>

    <h3>13. Data Storage, Syncing &amp; Backups</h3>
    <p>Mudo uses local-first storage in the user’s chosen folder, optional Google Drive syncing, and Supabase for account-level cloud data. Users are solely responsible for backing up their data, choosing safe storage locations, and managing privacy compliance. Mudo is not liable for data loss.</p>

    <h3>14. Subscription Plans, Trials &amp; Promo Codes</h3>
    <p>Studios may subscribe to paid features. Subscriptions renew automatically, are billed through Stripe, take effect immediately upon payment, and may change pricing with notice.</p>
    <p>Promo codes may provide discounted or free months, have no cash value, may be revoked upon abuse, and marketplace fees still apply during promo periods.</p>

    <h3>15. Intellectual Property</h3>
    <p>Mudo retains all rights to UI, components, branding, application code, and generated visual elements.</p>
    <p>Users own their uploaded assets, messages, listings, and studio data, and grant Mudo a licence to display content inside the platform.</p>

    <h3>16. Prohibited Use</h3>
    <p>You must NOT hack or interfere with the platform, reverse engineer, scrape or data-mine, upload harmful code, impersonate others, violate intellectual property, or attempt to bypass Stripe’s rules. Violations may result in immediate termination.</p>

    <h3>17. Limitation of Liability</h3>
    <p>To the fullest extent permitted by law, Mudo disclaims all warranties; is not liable for business losses; is not responsible for Seller or Instructor behaviour; is not liable for AI outputs; is not responsible for injuries in workshops; and does not guarantee uptime or uninterrupted service. Maximum liability to any user is limited to the fees paid to Mudo in the previous 12 months.</p>

    <h3>18. Governing Law</h3>
    <p>These Terms are governed by the laws of New South Wales, Australia. Disputes must be resolved in NSW courts unless required otherwise by consumer law.</p>

    <h3>19. Modifications to Terms</h3>
    <p>We may update these Terms with notice. Continued use constitutes acceptance.</p>

    <h3>20. Contact</h3>
    <p>
      Bark Line Studio<br />
      Email: <a href="mailto:hello@barklinestudio.com">hello@barklinestudio.com</a><br />
      Website: <a href="https://www.barklinestudio.com/" target="_blank" rel="noreferrer">https://www.barklinestudio.com/</a>
    </p>

    <h3>21. MONETISATION, FEES &amp; REVENUE SHARE</h3>
    <h4>21.1 Marketplace Revenue Share</h4>
    <p>For all Mudo Shop product sales: 95% to Seller, 5% to Mudo. Automatically deducted through Stripe Connect.</p>
    <h4>21.2 Workshops &amp; Classes</h4>
    <p>For Studio-hosted workshops: 95% to Studio/Instructor, 5% to Mudo. For Mudo-hosted workshops, Mudo retains full revenue.</p>
    <h4>21.3 Studio Space &amp; Resource Hire</h4>
    <p>Wheel time, kiln access, space rental: 95% to Studio, 5% to Mudo.</p>
    <h4>21.4 Subscriptions</h4>
    <p>Paid features across all worlds are subscription-based. Subscriptions renew automatically, may change with notice, and are independent of marketplace fees.</p>
    <h4>21.5 Promotions / Trials</h4>
    <p>Promo periods may reduce subscription cost but do not remove transaction fees.</p>
    <h4>21.6 Fee Changes</h4>
    <p>Mudo may alter fees with 14 days’ notice.</p>
    <h4>21.7 Taxes</h4>
    <p>Sellers must comply with all relevant tax laws. Mudo does not remit taxes on behalf of Sellers unless required by law.</p>
    <h4>21.8 Stripe Connect Requirement</h4>
    <p>Sellers must maintain an active Stripe Connect account. Mudo is not responsible for Stripe delays, holds, verification failures, or frozen funds.</p>
  </div>
);
